import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BarChart3,
  Users,
  Headphones,
  Heart,
  RefreshCw,
  Calendar,
  CalendarDays,
} from "lucide-react";
import { LoadingState } from "@/components/ui/loading-state";
import { LineChart, BarChart } from "@/components/ui/analytics-chart";

interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  trend?: number;
}

const MetricCard = ({
  title,
  value,
  description,
  icon,
  trend,
}: MetricCardProps) => (
  <Card className="bg-white/30 border-white/30">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-gray-800">
        {title}
      </CardTitle>
      <div className="h-8 w-8 rounded-full bg-white/30 flex items-center justify-center">
        {icon}
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      {description && (
        <p className="text-xs text-gray-600 mt-1">{description}</p>
      )}
      {trend !== undefined && (
        <div
          className={`text-xs mt-1 flex items-center ${trend >= 0 ? "text-green-600" : "text-red-600"}`}
        >
          {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}% from previous period
        </div>
      )}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalPodcasts: 0,
    totalEpisodes: 0,
    totalListens: 0,
    totalLikes: 0,
    activeUsers7d: 0,
    activeUsers30d: 0,
    dailyActiveUsers: 0,
    monthlyActiveUsers: 0,
    retentionRate: 0,
  });
  const [topPodcasts, setTopPodcasts] = useState<any[]>([]);
  const [topEpisodes, setTopEpisodes] = useState<any[]>([]);
  const [userTrends, setUserTrends] = useState<any[]>([]);
  const [listenTrends, setListenTrends] = useState<any[]>([]);

  useEffect(() => {
    // Redirect non-admin users
    if (!isAdmin) {
      navigate("/profile");
      return;
    }

    loadDashboardData();
  }, [isAdmin, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch basic metrics
      const [
        authUsersResponse,
        podcastsResponse,
        episodesResponse,
        listensResponse,
        likesResponse,
      ] = await Promise.all([
        // Count auth users from auth.users table via RPC function
        supabase.rpc("get_auth_users_count"),
        supabase.from("podcasts").select("id", { count: "exact" }),
        supabase.from("episodes").select("id", { count: "exact" }),
        supabase.from("listen_history").select("id", { count: "exact" }),
        supabase
          .from("liked_history")
          .select("id", { count: "exact" })
          .eq("interaction_type", "like"),
      ]);

      // Fetch active users (users with activity in the last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const [activeUsers7dResponse, activeUsers30dResponse] = await Promise.all(
        [
          // Active users in last 7 days (unique user_ids)
          supabase
            .from("listen_history")
            .select("user_id", { count: "exact", head: true })
            .gt("updated_at", sevenDaysAgo.toISOString())
            .not("user_id", "is", null),

          // Active users in last 30 days (unique user_ids)
          supabase
            .from("listen_history")
            .select("user_id", { count: "exact", head: true })
            .gt("updated_at", thirtyDaysAgo.toISOString())
            .not("user_id", "is", null),
        ],
      );

      // Fetch daily active users (from the last day)
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      const dailyActiveUsersResponse = await supabase
        .from("listen_history")
        .select("user_id", { count: "exact", head: true })
        .gt("updated_at", oneDayAgo.toISOString())
        .not("user_id", "is", null);

      // Fetch user activity trends (last 7 days)
      const { data: userTrendsData } = await supabase
        .from("daily_signups")
        .select("*")
        .order("day", { ascending: false })
        .limit(7);

      // Fetch listen trends (last 7 days)
      const { data: listenTrendsData } = await supabase
        .from("daily_listens")
        .select("*")
        .order("day", { ascending: false })
        .limit(7);

      // Fetch top podcasts by listens
      const topPodcastsResponse = await supabase
        .from("podcast_stats")
        .select(
          `
          id,
          title,
          image_url,
          subscriber_count,
          total_listens,
          total_likes
        `,
        )
        .order("total_listens", { ascending: false })
        .limit(5);

      // Fetch top episodes by listens
      const topEpisodesResponse = await supabase
        .from("episode_metrics")
        .select(
          `
          episode_id,
          listen_count,
          like_count,
          episodes!inner (title, image_url, podcast_id),
          podcasts:episodes.podcast_id (title)
        `,
        )
        .order("listen_count", { ascending: false })
        .limit(5);

      // Process user trends data
      const processedUserTrends = (userTrendsData || [])
        .reverse()
        .map((item) => ({
          label: new Date(item.day).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: item.signup_count || 0,
        }));

      // Process listen trends data
      const processedListenTrends = (listenTrendsData || [])
        .reverse()
        .map((item) => ({
          label: new Date(item.day).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: item.listen_count || 0,
        }));

      // Calculate retention rate (active users in last 30 days / total users)
      const totalUsers = authUsersResponse || 0;
      const activeUsers30d = activeUsers30dResponse.count || 0;
      const retentionRate =
        totalUsers > 0 ? Math.round((activeUsers30d / totalUsers) * 100) : 0;

      setMetrics({
        totalUsers: totalUsers,
        totalPodcasts: podcastsResponse.count || 0,
        totalEpisodes: episodesResponse.count || 0,
        totalListens: listensResponse.count || 0,
        totalLikes: likesResponse.count || 0,
        activeUsers7d: activeUsers7dResponse.count || 0,
        activeUsers30d: activeUsers30d,
        dailyActiveUsers: dailyActiveUsersResponse.count || 0,
        monthlyActiveUsers: activeUsers30d,
        retentionRate: retentionRate,
      });

      setTopPodcasts(topPodcastsResponse.data || []);
      setTopEpisodes(topEpisodesResponse.data || []);
      setUserTrends(processedUserTrends);
      setListenTrends(processedListenTrends);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    loadDashboardData();
  };

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <div className="w-full bg-white/5 backdrop-blur-md rounded-xl border border-white/20 shadow-xl overflow-hidden p-6">
        <LoadingState label="Loading analytics..." />
      </div>
    );
  }

  return (
    <div className="w-full bg-white/5 backdrop-blur-md rounded-xl border border-white/20 shadow-xl overflow-hidden flex flex-col h-full">
      <div className="flex-none p-4 border-b border-white/20">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/profile")}
              className="h-8 w-8 bg-white/20 hover:bg-white/30"
            >
              <ArrowLeft className="h-4 w-4 text-gray-800" />
            </Button>
            <h2 className="text-lg font-semibold text-gray-800">
              Analytics Dashboard
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-8 w-8 bg-white/20 hover:bg-white/30"
          >
            <RefreshCw
              className={`h-4 w-4 text-gray-800 ${refreshing ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4">
        <Tabs
          defaultValue="overview"
          className="w-full"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className="grid w-full grid-cols-3 bg-white/20 border border-white/30">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Total Users"
                value={metrics.totalUsers.toLocaleString()}
                icon={<Users className="h-4 w-4 text-gray-800" />}
              />
              <MetricCard
                title="Daily Active Users"
                value={metrics.dailyActiveUsers.toLocaleString()}
                description={
                  metrics.totalUsers > 0
                    ? `${Math.round((metrics.dailyActiveUsers / metrics.totalUsers) * 100)}% of total users`
                    : ""
                }
                icon={<Users className="h-4 w-4 text-gray-800" />}
                trend={5}
              />
              <MetricCard
                title="Monthly Active Users"
                value={metrics.monthlyActiveUsers.toLocaleString()}
                description={
                  metrics.totalUsers > 0
                    ? `${Math.round((metrics.monthlyActiveUsers / metrics.totalUsers) * 100)}% of total users`
                    : ""
                }
                icon={<CalendarDays className="h-4 w-4 text-gray-800" />}
                trend={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Total Listens"
                value={metrics.totalListens.toLocaleString()}
                icon={<Headphones className="h-4 w-4 text-gray-800" />}
                trend={8}
              />
              <MetricCard
                title="Total Likes"
                value={metrics.totalLikes.toLocaleString()}
                icon={<Heart className="h-4 w-4 text-gray-800" />}
                trend={2}
              />
              <MetricCard
                title="User Retention"
                value={`${metrics.retentionRate}%`}
                description="Monthly active users / total users"
                icon={<Users className="h-4 w-4 text-gray-800" />}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div>
                <h3 className="text-sm font-medium mb-2 text-gray-800">
                  User Signups (Last 7 Days)
                </h3>
                {userTrends.length > 0 ? (
                  <BarChart data={userTrends} height={200} barColor="#735fe2" />
                ) : (
                  <div className="bg-white/20 rounded-lg p-4 border border-white/30 h-[200px] flex items-center justify-center">
                    <p className="text-gray-600">No data available</p>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium mb-2 text-gray-800">
                  Listens (Last 7 Days)
                </h3>
                {listenTrends.length > 0 ? (
                  <LineChart
                    data={listenTrends}
                    height={200}
                    lineColor="#735fe2"
                  />
                ) : (
                  <div className="bg-white/20 rounded-lg p-4 border border-white/30 h-[200px] flex items-center justify-center">
                    <p className="text-gray-600">No data available</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MetricCard
                title="Total Users"
                value={metrics.totalUsers.toLocaleString()}
                icon={<Users className="h-4 w-4 text-gray-800" />}
              />
              <MetricCard
                title="User Retention"
                value={`${metrics.retentionRate}%`}
                description="Monthly active users / total users"
                icon={<Users className="h-4 w-4 text-gray-800" />}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Daily Active Users"
                value={metrics.dailyActiveUsers.toLocaleString()}
                description={
                  metrics.totalUsers > 0
                    ? `${Math.round((metrics.dailyActiveUsers / metrics.totalUsers) * 100)}% of total users`
                    : ""
                }
                icon={<Calendar className="h-4 w-4 text-gray-800" />}
              />
              <MetricCard
                title="Weekly Active Users"
                value={metrics.activeUsers7d.toLocaleString()}
                description={
                  metrics.totalUsers > 0
                    ? `${Math.round((metrics.activeUsers7d / metrics.totalUsers) * 100)}% of total users`
                    : ""
                }
                icon={<Calendar className="h-4 w-4 text-gray-800" />}
              />
              <MetricCard
                title="Monthly Active Users"
                value={metrics.activeUsers30d.toLocaleString()}
                description={
                  metrics.totalUsers > 0
                    ? `${Math.round((metrics.activeUsers30d / metrics.totalUsers) * 100)}% of total users`
                    : ""
                }
                icon={<CalendarDays className="h-4 w-4 text-gray-800" />}
              />
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2 text-gray-800">
                User Signups (Last 7 Days)
              </h3>
              {userTrends.length > 0 ? (
                <BarChart data={userTrends} height={200} barColor="#735fe2" />
              ) : (
                <div className="bg-white/20 rounded-lg p-4 border border-white/30 h-[200px] flex items-center justify-center">
                  <p className="text-gray-600">No data available</p>
                </div>
              )}
            </div>

            <div className="bg-white/20 rounded-lg p-4 border border-white/30">
              <h3 className="text-md font-semibold mb-3 text-gray-800">
                User Activity Definitions
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>
                  <strong>Daily Active Users (DAU):</strong> Unique users who
                  performed at least one listen action in the last 24 hours
                </li>
                <li>
                  <strong>Weekly Active Users (WAU):</strong> Unique users who
                  performed at least one listen action in the last 7 days
                </li>
                <li>
                  <strong>Monthly Active Users (MAU):</strong> Unique users who
                  performed at least one listen action in the last 30 days
                </li>
                <li>
                  <strong>Retention Rate:</strong> Percentage of total users who
                  have been active in the last 30 days
                </li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Total Podcasts"
                value={metrics.totalPodcasts.toLocaleString()}
                icon={<BarChart3 className="h-4 w-4 text-gray-800" />}
              />
              <MetricCard
                title="Total Episodes"
                value={metrics.totalEpisodes.toLocaleString()}
                description={
                  metrics.totalPodcasts > 0
                    ? `${Math.round(metrics.totalEpisodes / metrics.totalPodcasts)} avg per podcast`
                    : ""
                }
                icon={<BarChart3 className="h-4 w-4 text-gray-800" />}
              />
              <MetricCard
                title="Engagement Rate"
                value={`${metrics.totalListens > 0 ? Math.round((metrics.totalLikes / metrics.totalListens) * 100) : 0}%`}
                description="Likes / Listens ratio"
                icon={<Heart className="h-4 w-4 text-gray-800" />}
              />
            </div>

            <div>
              <h3 className="text-md font-semibold mb-3 text-gray-800">
                Top Podcasts
              </h3>
              <div className="space-y-3">
                {topPodcasts.map((podcast) => (
                  <div
                    key={podcast.id}
                    className="flex items-center gap-3 p-3 bg-white/20 rounded-lg border border-white/30 cursor-pointer"
                    onClick={() => navigate(`/podcast/${podcast.id}`)}
                  >
                    <img
                      src={
                        podcast.image_url ||
                        "https://dummyimage.com/48x48/f4f4f4/666666&text=Podcast"
                      }
                      alt={podcast.title}
                      className="w-12 h-12 rounded-md object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 truncate">
                        {podcast.title}
                      </h4>
                      <div className="flex gap-3 text-sm text-gray-600">
                        <span>
                          {podcast.subscriber_count?.toLocaleString() || 0}{" "}
                          subscribers
                        </span>
                        <span>•</span>
                        <span>
                          {podcast.total_listens?.toLocaleString() || 0} listens
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {topPodcasts.length === 0 && (
                  <p className="text-gray-600 text-center py-4">
                    No podcast data available
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-md font-semibold mb-3 text-gray-800">
                Top Episodes
              </h3>
              <div className="space-y-3">
                {topEpisodes.map((item) => (
                  <div
                    key={item.episode_id}
                    className="flex items-center gap-3 p-3 bg-white/20 rounded-lg border border-white/30"
                  >
                    <img
                      src={
                        item.episodes?.image_url ||
                        "https://dummyimage.com/48x48/f4f4f4/666666&text=Episode"
                      }
                      alt={item.episodes?.title}
                      className="w-12 h-12 rounded-md object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 truncate">
                        {item.episodes?.title}
                      </h4>
                      <div className="flex gap-3 text-sm text-gray-600">
                        <span>{item.podcasts?.title}</span>
                        <span>•</span>
                        <span>
                          {item.listen_count?.toLocaleString() || 0} listens
                        </span>
                        <span>•</span>
                        <span>
                          {item.like_count?.toLocaleString() || 0} likes
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {topEpisodes.length === 0 && (
                  <p className="text-gray-600 text-center py-4">
                    No episode data available
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2 text-gray-800">
                Listens (Last 7 Days)
              </h3>
              {listenTrends.length > 0 ? (
                <LineChart
                  data={listenTrends}
                  height={200}
                  lineColor="#735fe2"
                />
              ) : (
                <div className="bg-white/20 rounded-lg p-4 border border-white/30 h-[200px] flex items-center justify-center">
                  <p className="text-gray-600">No data available</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </ScrollArea>
    </div>
  );
};

export default Dashboard;
